import {
    Activity,
    AlertTriangle,
    ArrowRight,
    Building2,
    CheckCircle2,
    CircleDollarSign,
    Clock3,
    MoreHorizontal,
    Package,
    RefreshCcw,
    Smartphone,
    Store,
    TrendingUp,
    Users
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../services/api';
import './Overview.css';

const TENANT_STATUS_LABELS = {
    ACTIVE: 'Attivo',
    CONTRACT_SIGNED_PENDING_ACTIVATION: 'In attesa di attivazione',
    CONTRACT_SIGNED_PENDING_PAYMENT: 'In attesa di pagamento',
    REGISTERED: 'Registrato',
    SUSPENDED: 'Sospeso',
    CLOSED: 'Chiuso'
};

const DEVICE_STATUS_LABELS = {
    ACTIVE: 'Attivo',
    SHIPPED_PENDING_ACTIVATION: 'Spedito',
    PROVISIONED: 'Provisionato',
    DISABLED: 'Disabilitato'
};

const formatDate = (value) => new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
}).format(new Date(value));

const formatAmount = (amount, currency = 'EUR') => new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency
}).format(Number(amount || 0));

const Overview = () => {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInactiveSubscriptionPlans, setShowInactiveSubscriptionPlans] = useState(false);

    const loadOverview = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setOverview(await dashboardService.getOverview());
        } catch (err) {
            console.error('Failed to load dashboard overview:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    if (loading && !overview) {
        return (
            <div className="overview-loading">
                <div className="overview-spinner"></div>
                <p>Caricamento panoramica...</p>
            </div>
        );
    }

    const totals = overview?.totals || {};
    const tenantsByStatus = overview?.tenantsByStatus || {};
    const devicesByStatus = overview?.devicesByStatus || {};
    const subscriptions = overview?.subscriptions || { active: 0, byPlan: [] };
    const recentTenants = overview?.recentActivity?.tenants || [];
    const recentDevices = overview?.recentActivity?.devices || [];
    const recentVenues = overview?.recentActivity?.venues || [];
    const recentTips = overview?.recentActivity?.tips || [];
    const activeTenants = tenantsByStatus.ACTIVE || 0;
    const activeDevices = devicesByStatus.ACTIVE || 0;
    const activeSubscriptionPlans = (subscriptions.byPlan || []).filter((plan) => plan.active);
    const inactiveSubscriptionPlans = (subscriptions.byPlan || []).filter((plan) => !plan.active);
    const todayTips = overview?.todayTips || {
        currency: 'EUR',
        count: 0,
        totalAmount: 0,
        soontipFee: 0
    };

    const kpis = [
        {
            label: 'Tenant attivi',
            value: activeTenants,
            meta: `${totals.tenants || 0} totali`,
            icon: Building2,
            tone: 'pink',
            path: '/admin/tenants'
        },
        {
            label: 'Abbonamenti attivi',
            value: subscriptions.active || 0,
            details: activeSubscriptionPlans,
            hiddenDetails: inactiveSubscriptionPlans,
            emptyDetails: 'Nessun abbonamento su piani attivi',
            icon: Package,
            tone: 'orange',
            path: '/admin/plans'
        },
        {
            label: 'Locali',
            value: totals.activeTenantVenues || 0,
            meta: 'Solo dei tenant attivi',
            icon: Store,
            tone: 'blue',
            path: '/admin/venues'
        },
        {
            label: 'Workers connessi',
            value: totals.connectedWorkers || 0,
            meta: `${totals.workers || 0} totali`,
            icon: Users,
            tone: 'purple',
            path: '/admin/workers'
        },
        {
            label: 'Devices attivi',
            value: activeDevices,
            meta: `${totals.devices || 0} totali`,
            icon: Smartphone,
            tone: 'green',
            path: '/admin/devices'
        }
    ];

    const quickActions = [
        {
            label: 'Gestisci tenant',
            description: 'Anagrafiche, attivazioni e stato onboarding',
            path: '/admin/tenants',
            icon: Building2,
            tone: 'pink'
        },
        {
            label: 'Monitora devices',
            description: 'Health, attivazione e configurazione kiosk',
            path: '/admin/devices',
            icon: Smartphone,
            tone: 'purple'
        },
        {
            label: 'Controlla transfers',
            description: 'Pagamenti, transfer e fatturazione',
            path: '/admin/tip-transactions',
            icon: TrendingUp,
            tone: 'green'
        }
    ];

    return (
        <div className="overview-page">
            <header className="overview-page-header">
                <span className="overview-page-header-icon">
                    <Activity size={21} aria-hidden="true" />
                </span>
                <div>
                    <h1>Overview</h1>
                    <p>Stato globale e attività recente della piattaforma SoonTip</p>
                </div>
                <button
                    className="overview-refresh"
                    onClick={loadOverview}
                    disabled={loading}
                >
                    <RefreshCcw size={15} className={loading ? 'overview-spin' : ''} />
                    Aggiorna
                </button>
            </header>

            {error && (
                <div className="overview-alert overview-alert--error">
                    <AlertTriangle size={18} />
                    <span><strong>Impossibile aggiornare i dati.</strong> {error}</span>
                </div>
            )}

            {(overview?.unresolvedReversals || 0) > 0 && (
                <Link to="/admin/credit-notes" className="overview-alert overview-alert--warning">
                    <AlertTriangle size={19} />
                    <span>
                        <strong>{overview.unresolvedReversals} reversal da documentare</strong>
                        {' '}con una nota di credito.
                    </span>
                    <ArrowRight size={17} />
                </Link>
            )}

            <section className="overview-kpi-grid" aria-label="Indicatori principali">
                {kpis.map(({ icon: Icon, ...kpi }) => {
                    const visibleDetails = kpi.details
                        ? [
                            ...kpi.details,
                            ...(showInactiveSubscriptionPlans ? (kpi.hiddenDetails || []) : [])
                        ]
                        : null;
                    const cardContent = (
                        <>
                            <span className="overview-kpi-icon">
                                <Icon size={21} />
                            </span>
                            <span className="overview-kpi-copy">
                                <span className="overview-kpi-label">{kpi.label}</span>
                                <strong>{kpi.value}</strong>
                                {visibleDetails ? (
                                    <span className="overview-kpi-breakdown">
                                        {visibleDetails.length > 0 ? visibleDetails.map((detail) => (
                                            <span
                                                key={detail.id}
                                                className={!detail.active ? 'overview-kpi-plan--inactive' : undefined}
                                            >
                                                <span>{detail.name}</span>
                                                <strong>{detail.count}</strong>
                                            </span>
                                        )) : (
                                            <span className="overview-kpi-meta">{kpi.emptyDetails}</span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="overview-kpi-meta">{kpi.meta}</span>
                                )}
                            </span>
                            {!kpi.hiddenDetails && <ArrowRight size={17} className="overview-kpi-arrow" />}
                        </>
                    );

                    if (kpi.hiddenDetails) {
                        return (
                            <article
                                key={kpi.label}
                                className={`overview-kpi-card overview-kpi-card--${kpi.tone} overview-kpi-card--expandable`}
                            >
                                <Link to={kpi.path} className="overview-kpi-card-main">
                                    {cardContent}
                                </Link>
                                {kpi.hiddenDetails.length > 0 && (
                                    <button
                                        type="button"
                                        className="overview-kpi-more"
                                        onClick={() => setShowInactiveSubscriptionPlans((current) => !current)}
                                        aria-expanded={showInactiveSubscriptionPlans}
                                        aria-label={showInactiveSubscriptionPlans
                                            ? 'Nascondi i piani non più attivi'
                                            : `Mostra ${kpi.hiddenDetails.length} piani non più attivi`}
                                        title={showInactiveSubscriptionPlans
                                            ? 'Nascondi piani non attivi'
                                            : 'Mostra piani non attivi'}
                                    >
                                        <MoreHorizontal size={18} />
                                    </button>
                                )}
                            </article>
                        );
                    }

                    return (
                        <Link
                            key={kpi.label}
                            to={kpi.path}
                            className={`overview-kpi-card overview-kpi-card--${kpi.tone}`}
                        >
                            {cardContent}
                        </Link>
                    );
                })}
            </section>

            <Link to="/admin/tip-transactions" className="overview-today-card">
                <span className="overview-today-icon">
                    <CircleDollarSign size={23} />
                </span>
                <span className="overview-today-heading">
                    <span className="overview-eyebrow">Solo pagamenti riusciti</span>
                    <strong>Mance di oggi</strong>
                    <small>Periodo calcolato secondo il fuso Europe/Rome</small>
                </span>
                <span className="overview-today-metrics">
                    <span>
                        <small>Mance ricevute</small>
                        <strong>{todayTips.count}</strong>
                    </span>
                    <span>
                        <small>Importo totale</small>
                        <strong>{formatAmount(todayTips.totalAmount, todayTips.currency)}</strong>
                    </span>
                    <span>
                        <small>Fee SoonTip (IVA inclusa)</small>
                        <strong>{formatAmount(todayTips.soontipFee, todayTips.currency)}</strong>
                    </span>
                </span>
                <ArrowRight size={18} className="overview-today-arrow" />
            </Link>

            <div className="overview-content-grid">
                <section className="overview-panel overview-panel--activity">
                    <div className="overview-panel-header">
                        <div>
                            <span className="overview-eyebrow">Attività recente</span>
                            <h2>Nuovi tenant</h2>
                        </div>
                        <Link to="/admin/tenants">Vedi tutti <ArrowRight size={14} /></Link>
                    </div>

                    <div className="overview-activity-list">
                        {recentTenants.length > 0 ? recentTenants.map((tenant) => (
                            <Link key={tenant.id} to={`/admin/tenant/${tenant.id}`} className="overview-activity-row">
                                <span className="overview-activity-icon overview-activity-icon--tenant">
                                    <Building2 size={16} />
                                </span>
                                <span className="overview-activity-main">
                                    <strong>{tenant.company_name}</strong>
                                    <span>{tenant._count?.venues || 0} locali · {tenant._count?.workers || 0} workers</span>
                                </span>
                                <span className="overview-activity-side">
                                    <span className={`overview-status overview-status--${String(tenant.status || '').toLowerCase()}`}>
                                        {TENANT_STATUS_LABELS[tenant.status] || tenant.status}
                                    </span>
                                    <time>{formatDate(tenant.createdAt)}</time>
                                </span>
                            </Link>
                        )) : (
                            <div className="overview-empty">Nessun tenant recente.</div>
                        )}
                    </div>
                </section>

                <section className="overview-panel overview-panel--activity">
                    <div className="overview-panel-header">
                        <div>
                            <span className="overview-eyebrow">Attività recente</span>
                            <h2>Nuovi locali</h2>
                        </div>
                        <Link to="/admin/venues">Vedi tutti <ArrowRight size={14} /></Link>
                    </div>

                    <div className="overview-activity-list">
                        {recentVenues.length > 0 ? recentVenues.map((venue) => (
                            <Link key={venue.id} to={`/admin/venue/${venue.id}`} className="overview-activity-row">
                                <span className="overview-activity-icon overview-activity-icon--venue">
                                    <Store size={16} />
                                </span>
                                <span className="overview-activity-main">
                                    <strong>{venue.name}</strong>
                                    <span>{venue.tenant?.company_name || venue.address || 'Tenant non disponibile'}</span>
                                </span>
                                <span className="overview-activity-side">
                                    <span className="overview-activity-count">
                                        {venue._count?.workers || 0} workers · {venue._count?.devices || 0} devices
                                    </span>
                                    <time>{formatDate(venue.createdAt)}</time>
                                </span>
                            </Link>
                        )) : (
                            <div className="overview-empty">Nessun locale recente.</div>
                        )}
                    </div>
                </section>

                <section className="overview-panel overview-panel--activity">
                    <div className="overview-panel-header">
                        <div>
                            <span className="overview-eyebrow">Attività recente</span>
                            <h2>Nuovi devices</h2>
                        </div>
                        <Link to="/admin/devices">Vedi tutti <ArrowRight size={14} /></Link>
                    </div>

                    <div className="overview-activity-list">
                        {recentDevices.length > 0 ? recentDevices.map((device) => (
                            <Link key={device.id} to="/admin/devices" className="overview-activity-row">
                                <span className="overview-activity-icon overview-activity-icon--device">
                                    <Smartphone size={16} />
                                </span>
                                <span className="overview-activity-main">
                                    <strong>{device.label || device.setupCode}</strong>
                                    <span>
                                        {device.setupCode}
                                        {device.venue?.name ? ` · ${device.venue.name}` : ''}
                                    </span>
                                </span>
                                <span className="overview-activity-side">
                                    <span className={`overview-status overview-status--${String(device.status || '').toLowerCase()}`}>
                                        {DEVICE_STATUS_LABELS[device.status] || device.status}
                                    </span>
                                    <time>{formatDate(device.createdAt)}</time>
                                </span>
                            </Link>
                        )) : (
                            <div className="overview-empty">Nessun device recente.</div>
                        )}
                    </div>
                </section>

                <section className="overview-panel overview-panel--activity">
                    <div className="overview-panel-header">
                        <div>
                            <span className="overview-eyebrow">Attività recente</span>
                            <h2>Ultime mance</h2>
                        </div>
                        <Link to="/admin/tip-transactions">Vedi tutte <ArrowRight size={14} /></Link>
                    </div>

                    <div className="overview-activity-list">
                        {recentTips.length > 0 ? recentTips.map((tip) => (
                            <Link key={tip.id} to="/admin/tip-transactions" className="overview-activity-row">
                                <span className="overview-activity-icon overview-activity-icon--tip">
                                    <CircleDollarSign size={16} />
                                </span>
                                <span className="overview-activity-main">
                                    <strong className="overview-tip-amount">{formatAmount(tip.amount, tip.currency)}</strong>
                                    <span>
                                        {tip.venue?.name || 'Locale non disponibile'}
                                        {tip.venue?.tenant?.company_name ? ` · ${tip.venue.tenant.company_name}` : ''}
                                    </span>
                                </span>
                                <span className="overview-activity-side">
                                    <span className="overview-channel">
                                        {tip.collectionChannel === 'TIP_LINK_WEB' ? 'Tip Link' : 'Totem'}
                                    </span>
                                    <time>{formatDate(tip.transactionDate)}</time>
                                </span>
                            </Link>
                        )) : (
                            <div className="overview-empty">Nessuna mancia recente.</div>
                        )}
                    </div>
                </section>
            </div>

            <section className="overview-quick-section">
                <div className="overview-section-heading">
                    <span className="overview-eyebrow">Navigazione</span>
                    <h2>Azioni rapide</h2>
                </div>
                <div className="overview-quick-grid">
                    {quickActions.map(({ icon: Icon, ...action }) => (
                        <Link
                            key={action.path}
                            to={action.path}
                            className={`overview-quick-card overview-quick-card--${action.tone}`}
                        >
                            <span className="overview-quick-icon"><Icon size={19} /></span>
                            <span>
                                <strong>{action.label}</strong>
                                <small>{action.description}</small>
                            </span>
                            <ArrowRight size={17} />
                        </Link>
                    ))}
                </div>
            </section>

            <div className="overview-system-status">
                <CheckCircle2 size={16} />
                <span>Dashboard sincronizzata</span>
                <Clock3 size={14} />
                <time>{new Date().toLocaleString('it-IT')}</time>
            </div>
        </div>
    );
};

export default Overview;
